import { format, parseISO, isValid } from 'date-fns';

/**
 * Formats a UTC date string to a compact local browser time format for table display
 * @param utcDateString - UTC date string from backend
 * @param formatString - Optional format string (default: 'MM/dd/yy h:mma')
 * @returns Formatted date string or 'Never' if invalid/empty
 */
export const formatUTCToLocal = (
  utcDateString: string | null | undefined,
  formatString: string = 'MM/dd/yy h:mma',
): string => {
  if (!utcDateString || utcDateString === 'Never' || utcDateString === '') {
    return 'Never';
  }

  try {
    // Parse the UTC date string
    const date = parseISO(utcDateString);

    // Check if the date is valid
    if (!isValid(date)) {
      return 'Never';
    }

    // Format to local browser time
    return format(date, formatString);
  } catch (error) {
    console.warn('Error formatting date:', utcDateString, error);
    return 'Never';
  }
};

/**
 * Formats a UTC date string to a compact short date format for table display
 * @param utcDateString - UTC date string from backend
 * @returns Short formatted date string or 'Never' if invalid/empty
 */
export const formatUTCToShortDate = (utcDateString: string | null | undefined): string => {
  return formatUTCToLocal(utcDateString, 'MM/dd/yy');
};

/**
 * Formats a UTC date string to a full date and time format
 * @param utcDateString - UTC date string from backend
 * @returns Full formatted date string or 'Never' if invalid/empty
 */
export const formatUTCToFullDateTime = (utcDateString: string | null | undefined): string => {
  return formatUTCToLocal(utcDateString, 'MMM dd, yyyy h:mm:ss a');
};

/**
 * Formats a UTC date string to just time format
 * @param utcDateString - UTC date string from backend
 * @returns Time formatted string or 'Never' if invalid/empty
 */
export const formatUTCToTime = (utcDateString: string | null | undefined): string => {
  return formatUTCToLocal(utcDateString, 'h:mm a');
};
